import * as React from 'react';
import MenuBar from './routes/MenuBar';
import ErrorBoundary from './widgets/ErrorBoundary';
import GlobalStatusReporter from './widgets/GlobalStatusReporter';
import UnhandledError from './widgets/UnhandledError';
import LoginStatus from './security/LoginStatus';
import { AwthProvider, AwthProviderProps } from './awth/AwthProvider';
import { AppProps, AwthenticatedRoutes } from './App';
import { Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { RolesAndServices } from './selectors/selectRolesAndServices';
import { GlobalStatusType } from './types';
import MaskedLoadingIndicator from './widgets/MaskedLoadingIndicator';
import { ROUTE_PATH } from './constants';
import { useSelector } from 'react-redux';
import { sessionSelector } from './reducers/session';

export const MenuAndStatus: React.FC<{
  rolesAndServices: RolesAndServices;
  globalStatus: Array<GlobalStatusType>;
  isSwitchingCustomer: boolean;
  clearGlobalStatus: (serial: number) => void;
}> = ({
  rolesAndServices,
  globalStatus,
  clearGlobalStatus,
  isSwitchingCustomer,
}) => {
  const { isAuthenticated } = useAuth0();
  const username = useSelector(sessionSelector.getUsername);
  const loggedIn = !!username;

  return (
    <>
      <MenuBar
        isAuth0Logout={!isAuthenticated}
        rolesAndServices={rolesAndServices}
      />
      <GlobalStatusReporter
        status={globalStatus}
        onDismiss={clearGlobalStatus}
      />
      {!loggedIn && isAuthenticated ? (
        <MaskedLoadingIndicator
          message={isSwitchingCustomer ? 'Switching customer' : 'Connecting'}
        />
      ) : null}
    </>
  );
};

export type AppWithStatusProps = AppProps & AwthProviderProps;

export const AppWithStatus: React.FC<AppWithStatusProps> = ({
  clearGlobalStatus,
  globalStatus,
  username,
  isSwitchingCustomer,
  rolesAndServices,
  auth0Config,
  loginConfig,
}) => {
  return (
    <AwthProvider
      auth0Config={auth0Config}
      loginConfig={loginConfig}
      unAuthenticatedNode={
        <MenuAndStatus
          globalStatus={globalStatus}
          isSwitchingCustomer={isSwitchingCustomer}
          rolesAndServices={rolesAndServices}
          clearGlobalStatus={clearGlobalStatus}
        />
      }
      unAuthenticatedRoute={
        <Route exact path={ROUTE_PATH.LOGIN} component={LoginStatus} />
      }
    >
      <ErrorBoundary errorComponent={<UnhandledError />}>
        {!!username ? <AwthenticatedRoutes /> : null}
      </ErrorBoundary>
    </AwthProvider>
  );
};

export default AppWithStatus;
